import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { PluginOptionsByType } from "chart.js";
import { _DeepPartialObject } from "chart.js/types/utils";
import "./Ativos.css";

import { ClientePageShell } from "../../../components/ClientePageShell";
import { GraficoPizza } from "../../../components/Graficos/GraficoPizza";
import { Loading } from "../../../components/Loading";
import { ModalDesconectar } from "../../../components/modais/ModalDesconectar";
import {
    ServicoFinanceiroClienteContext,
    usuarioServicoFinanceiroContextType,
} from "../../../context/servicoFinanceiroCliente";
import {
    CarteiraAtivosData,
    carregarAtivosCarteira,
    normalizeAssetSymbol,
} from "./functions";
import { formatOmexDate } from "./formatters";
import {
    obterDetalheTickerOmexPro,
    obterEtfInflowsLatestOmexPro,
    obterMacroFeedOmexPro,
    OmexProEtfInflowReport,
    OmexProMacroItem,
    OmexProTickerDetail,
} from "../../../services/omexpro";

import {
    FiBriefcase,
    FiClock,
    FiLayers,
    FiRefreshCw,
    FiTarget,
    FiTrendingUp,
    FiZap,
} from "react-icons/fi";

export interface ativoFinanceiro {
    nome: string;
    sigla: string;
    origem: string;
    porcentagemDoAtivoNaCarteira: number;
}

const emptyAtivosData: CarteiraAtivosData = {
    ativos: [],
    labels: [],
    porcentagem: [],
    colorArray: [],
    marketDataCount: 0,
};

const formatCurrency = (value?: number, currency = "BRL") => {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
        return "-";
    }

    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency,
        maximumFractionDigits: currency === "USD" ? 2 : 2,
    }).format(Number(value));
};

const formatCompactCurrency = (value?: number, currency = "USD") => {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
        return "-";
    }

    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency,
        notation: "compact",
        maximumFractionDigits: 2,
    }).format(Number(value));
};

const formatPercent = (value?: number) => {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
        return "-";
    }

    return `${Number(value).toFixed(2)}%`;
};

const formatDateTime = (date?: string) => {
    if (!date) {
        return "Sem atualização de mercado";
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
        return "Atualização indisponível";
    }

    return parsedDate.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const toNumber = (value: unknown) => {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : undefined;
};

const getTrendClass = (value?: number) => {
    if (value === undefined || value === null) {
        return "";
    }

    return value >= 0 ? "positive" : "negative";
};

const getFlowClass = (value?: number) => {
    if (value === undefined || value === 0) {
        return "neutral";
    }

    return value > 0 ? "positive" : "negative";
};

const extractEtfAssets = (report?: OmexProEtfInflowReport) => {
    if (!report) {
        return [];
    }

    if (Array.isArray(report.assets)) {
        return report.assets;
    }

    if (report.assetsByName && typeof report.assetsByName === "object") {
        return Object.values(report.assetsByName);
    }

    return [];
};

const toDisplayText = (value: unknown, fallback = "-") => {
    if (value === undefined || value === null || value === "") {
        return fallback;
    }

    if (typeof value === "string" || typeof value === "number") {
        return String(value);
    }

    if (typeof value === "object") {
        const entries = Object.entries(value as Record<string, unknown>)
            .filter(([, entryValue]) => entryValue !== undefined && entryValue !== null && entryValue !== "")
            .slice(0, 3)
            .map(([key, entryValue]) => `${key}: ${String(entryValue)}`);

        return entries.length ? entries.join(" / ") : fallback;
    }

    return fallback;
};

const getEtfAssetLabel = (asset: any, index: number) =>
    toDisplayText(asset.asset || asset.name || asset.symbol || asset.base_symbol, `Ativo ${index + 1}`);

const getEtfAssetFlowValue = (asset: any) =>
    toNumber(asset.totalFlow ?? asset.total_flow);

const formatEtfFlow = (value?: number) => {
    if (value === undefined) {
        return "-";
    }

    const signal = value > 0 ? "+" : value < 0 ? "-" : "";
    const formattedValue = Math.abs(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return `${signal}US$ ${formattedValue} mi`;
};

const getEtfFundFlows = (asset: any) => {
    const flows = asset.netflows || asset.formattedNetflows || asset.formatted_netflows;

    if (!flows || typeof flows !== "object") {
        return [];
    }

    return Object.entries(flows)
        .map(([fund, value]) => ({ fund, value: toNumber(value) }))
        .filter((flow) => flow.value !== undefined && flow.value !== 0)
        .sort((a, b) => Math.abs(b.value || 0) - Math.abs(a.value || 0));
};

const feedTypeLabels: Record<string, string> = {
    macro_note: "Notas em Macro",
    etf_inflow: "Flows em ETF",
};

const macroTagLabels: Record<string, string> = {
    employment: "Emprego",
    growth: "Crescimento",
    inflation: "Inflação",
    inflows: "Fluxos",
    liquidity: "Liquidez",
    macro: "Macro",
    rates: "Juros",
    risk: "Risco",
};

const getFeedTypeLabel = (item: OmexProMacroItem) => {
    const type = item.type || item.source || "macro_note";

    return feedTypeLabels[type] || type.replace(/_/g, " ");
};

const getMacroNote = (item: OmexProMacroItem) => {
    const payload = item.payload || {};
    const fullNote = item.fullNote || payload.fullNote || payload.cleanContent || payload.content || item.summary || "";
    const title = item.title?.trim();

    if (title && fullNote.trim().startsWith(title)) {
        return fullNote.trim().slice(title.length).trim();
    }

    return fullNote.trim();
};

const getMacroImages = (item: OmexProMacroItem) => {
    const payloadImages = Array.isArray(item.payload?.images) ? item.payload.images : [];
    const images = [...(item.images || []), ...payloadImages];

    return Array.from(new Set(images.map((image) => {
        if (typeof image === "string") {
            return image;
        }

        return String(image.storage_url || image.url || image.source_url || "");
    }).filter(Boolean)));
};

const getMacroAssets = (item: OmexProMacroItem) => {
    const assets = item.assets?.length ? item.assets : item.payload?.assets;

    return Array.isArray(assets) ? assets.map(String).filter(Boolean) : [];
};

const getMacroTags = (item: OmexProMacroItem) => {
    const tags = item.tags?.length ? item.tags : item.payload?.tags;

    return Array.isArray(tags)
        ? tags.map((tag) => macroTagLabels[String(tag).toLowerCase()] || String(tag))
        : [];
};

const assetMatchesMacro = (item: OmexProMacroItem, symbols: string[]) => {
    const itemAssets = getMacroAssets(item).map((asset) => normalizeAssetSymbol(asset));
    const text = `${item.title || ""} ${getMacroNote(item)} ${getMacroTags(item).join(" ")}`.toUpperCase();

    return symbols.some((symbol) => itemAssets.includes(symbol) || text.includes(symbol));
};

const getAssetDetailUpdatedAt = (detail?: OmexProTickerDetail) =>
    detail?.market?.updated_at ||
    detail?.derivatives?.updated_at ||
    detail?.depth?.snapshot_time;

const getDistinctAssetName = (symbol: string, displayName?: string) => {
    const name = displayName?.trim();

    if (!name) {
        return undefined;
    }

    const normalizedSymbol = normalizeAssetSymbol(symbol);
    const normalizedName = normalizeAssetSymbol(name);

    return normalizedName === normalizedSymbol ? undefined : name;
};

const getAssetImageUrl = (
    asset?: CarteiraAtivosData["ativos"][number],
    detail?: OmexProTickerDetail
) => {
    const detailAsset = detail?.asset as Record<string, unknown> | undefined;
    const detailMarket = detail?.market as Record<string, unknown> | undefined;
    const candidates = [
        detailAsset?.image_url,
        detailAsset?.imageUrl,
        detailAsset?.logo_url,
        detailMarket?.image_url,
        asset?.market?.image_url,
    ];

    const apiImageUrl = candidates.find(
        (candidate): candidate is string => typeof candidate === "string" && candidate.trim().length > 0
    );

    if (apiImageUrl) {
        return apiImageUrl;
    }

    const symbol = normalizeAssetSymbol(
        asset?.symbol || String(detailAsset?.base_symbol || detailAsset?.symbol || "")
    );

    return symbol
        ? `https://assets.coincap.io/assets/icons/${symbol.toLowerCase()}@2x.png`
        : undefined;
};

const AssetIcon = ({
    symbol,
    imageUrl,
    className,
}: {
    symbol: string;
    imageUrl?: string;
    className?: string;
}) => {
    const [imageFailed, setImageFailed] = useState(false);

    useEffect(() => {
        setImageFailed(false);
    }, [imageUrl]);

    if (!imageUrl || imageFailed) {
        return <span className={className}>{symbol.slice(0, 2)}</span>;
    }

    if (className) {
        return (
            <span className={`${className} has-image`}>
                <img
                    src={imageUrl}
                    alt={`Logo ${symbol}`}
                    onError={() => setImageFailed(true)}
                />
            </span>
        );
    }

    return (
        <img
            src={imageUrl}
            alt={`Logo ${symbol}`}
            onError={() => setImageFailed(true)}
        />
    );
};

export const Ativos = () => {
    const [ativosData, setAtivosData] = useState<CarteiraAtivosData>(emptyAtivosData);
    const [macroFeed, setMacroFeed] = useState<OmexProMacroItem[]>([]);
    const [etfReport, setEtfReport] = useState<OmexProEtfInflowReport | undefined>();
    const [selectedSymbol, setSelectedSymbol] = useState<string>("");
    const [selectedDetail, setSelectedDetail] = useState<OmexProTickerDetail | undefined>();
    const [reloadPage, setReloadPage] = useState<boolean>(true);
    const [modalDesconectar, setModalDesconectar] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [detailLoading, setDetailLoading] = useState<boolean>(false);
    const [erroPage, setErroPage] = useState<boolean>(false);
    const [detailError, setDetailError] = useState<boolean>(false);

    const { reloadServicoFinanceiro, usuarioServicoFinanceiro } =
        useContext<usuarioServicoFinanceiroContextType>(ServicoFinanceiroClienteContext);

    const symbols = useMemo(() => ativosData.ativos.map((asset) => asset.symbol), [ativosData.ativos]);
    const selectedAsset = useMemo(
        () => ativosData.ativos.find((asset) => asset.symbol === selectedSymbol) || ativosData.ativos[0],
        [ativosData.ativos, selectedSymbol]
    );
    const matchedMacroFeed = useMemo(() => {
        const matchedItems = macroFeed.filter((item) => assetMatchesMacro(item, symbols));

        return matchedItems.length ? matchedItems : macroFeed;
    }, [macroFeed, symbols]);
    const etfAssets = useMemo(() => extractEtfAssets(etfReport), [etfReport]);
    const etfFlowTotal = useMemo(
        () => etfAssets.reduce((total, asset) => total + (getEtfAssetFlowValue(asset) || 0), 0),
        [etfAssets]
    );
    const etfMaxFlow = useMemo(
        () => Math.max(...etfAssets.map((asset) => Math.abs(getEtfAssetFlowValue(asset) || 0)), 1),
        [etfAssets]
    );
    const etfPositiveAssets = useMemo(
        () => etfAssets.filter((asset) => (getEtfAssetFlowValue(asset) || 0) > 0).length,
        [etfAssets]
    );
    const etfNegativeAssets = useMemo(
        () => etfAssets.filter((asset) => (getEtfAssetFlowValue(asset) || 0) < 0).length,
        [etfAssets]
    );
    const selectedMarket = selectedDetail?.market || selectedAsset?.market;
    const selectedDerivatives = selectedDetail?.derivatives;
    const selectedOrderBook = selectedDetail?.order_book;
    const selectedAssetName = selectedAsset
        ? getDistinctAssetName(selectedAsset.symbol, selectedAsset.displayName)
        : undefined;
    const selectedAssetImageUrl = getAssetImageUrl(selectedAsset, selectedDetail);

    const carregarAtivos = useCallback(async () => {
        setLoading(true);
        setErroPage(false);

        try {
            const carteira = await carregarAtivosCarteira(usuarioServicoFinanceiro, setModalDesconectar);
            const carteiraSymbols = carteira.ativos.map((asset) => asset.symbol);
            const [macroResult, etfResult] = await Promise.allSettled([
                obterMacroFeedOmexPro(8),
                obterEtfInflowsLatestOmexPro(),
            ]);

            setAtivosData(carteira);
            setMacroFeed(macroResult.status === "fulfilled" ? macroResult.value : []);
            setEtfReport(etfResult.status === "fulfilled" ? etfResult.value : undefined);
            setSelectedSymbol((currentSymbol) =>
                carteiraSymbols.includes(currentSymbol) ? currentSymbol : carteiraSymbols[0] || ""
            );
        } catch {
            setAtivosData(emptyAtivosData);
            setMacroFeed([]);
            setEtfReport(undefined);
            setErroPage(true);
        } finally {
            setLoading(false);
        }
    }, [usuarioServicoFinanceiro]);

    useEffect(() => {
        carregarAtivos();
    }, [carregarAtivos, reloadPage, reloadServicoFinanceiro]);

    useEffect(() => {
        if (!selectedSymbol) {
            setSelectedDetail(undefined);
            return;
        }

        let active = true;
        setDetailLoading(true);
        setDetailError(false);

        obterDetalheTickerOmexPro(selectedSymbol)
            .then((detail) => {
                if (active) {
                    setSelectedDetail(detail);
                }
            })
            .catch(() => {
                if (active) {
                    setSelectedDetail(undefined);
                    setDetailError(true);
                }
            })
            .finally(() => {
                if (active) {
                    setDetailLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [selectedSymbol]);

    const plugins: _DeepPartialObject<PluginOptionsByType<"pie">> = useMemo(() => ({
        tooltip: {
            enabled: true,
            mode: "point",
            callbacks: {
                title: (data) => data.map((dataItem) => dataItem.label),
                label: (data) => {
                    const valorPorcentagem = ativosData.porcentagem[data.dataIndex] ?? 0;
                    return [`Participação na carteira: ${valorPorcentagem.toFixed(2)}%`];
                },
            },
        },
    }), [ativosData.porcentagem]);

    const handleReload = () => setReloadPage((currentValue) => !currentValue);

    return (
        <>
            {loading && <Loading />}
            <ClientePageShell
                className="ativos-page"
                eyebrow="Carteira"
                title="Ativos"
                subtitle="Acompanhe a composição percentual da carteira e a telemetria de mercado da Omex Capital."
                actions={
                    <button className="cliente-page-button secondary" type="button" onClick={handleReload}>
                        <FiRefreshCw />
                        Atualizar
                    </button>
                }
            >
                <section className="ativos-overview-grid">
                    <div className="ativos-chart-panel">
                        {erroPage ? (
                            <div className="cliente-page-panel cliente-page-empty">
                                <p>Não foi possível carregar a composição da carteira.</p>
                                <button className="cliente-page-button" type="button" onClick={handleReload}>
                                    <FiRefreshCw />
                                    Tentar novamente
                                </button>
                            </div>
                        ) : ativosData.ativos.length ? (
                            <GraficoPizza
                                colorArray={ativosData.colorArray}
                                labels={ativosData.labels}
                                porcentagens={ativosData.porcentagem}
                                plugins={plugins}
                                tituloGrafico="Composição da carteira"
                                subTituloGrafico="Todos os ativos encontrados no último fechamento do usuário"
                            />
                        ) : (
                            <div className="cliente-page-panel cliente-page-empty">
                                <p>Nenhum ativo encontrado na carteira selecionada.</p>
                            </div>
                        )}
                    </div>

                </section>

                <section className="ativos-wallet-section">
                    <div className="cliente-page-section-header">
                        <div>
                            <p>Wallet</p>
                            <h2>Ativos da carteira</h2>
                        </div>
                        <span>
                            {ativosData.ativos.length} ativos monitorados. Mercado atualizado em {formatDateTime(ativosData.marketUpdatedAt)}.
                        </span>
                    </div>

                    <div className="ativos-wallet-grid">
                        {ativosData.ativos.map((asset) => {
                            const assetName = getDistinctAssetName(asset.symbol, asset.displayName);
                            const assetImageUrl = getAssetImageUrl(
                                asset,
                                selectedAsset?.symbol === asset.symbol ? selectedDetail : undefined
                            );

                            return (
                                <button
                                    className={`cliente-page-panel ativos-wallet-card ${selectedAsset?.symbol === asset.symbol ? "active" : ""}`}
                                    key={`${asset.symbol}-${asset.origem}`}
                                    type="button"
                                    onClick={() => setSelectedSymbol(asset.symbol)}
                                >
                                <div className="ativos-wallet-card-header">
                                    <div className="ativos-wallet-identity">
                                        <AssetIcon symbol={asset.symbol} imageUrl={assetImageUrl} />
                                        <div>
                                            <strong>{asset.symbol}</strong>
                                            {assetName && <small>{assetName}</small>}
                                        </div>
                                    </div>
                                    <div className="ativos-wallet-allocation">
                                        <span>{formatPercent(asset.porcentagemDoAtivoNaCarteira)}</span>
                                        <small>da carteira</small>
                                    </div>
                                </div>

                                <div className="ativos-wallet-stats">
                                    <div>
                                        <span>Preço mercado</span>
                                        <strong>{formatCurrency(asset.marketPrice, "USD")}</strong>
                                    </div>
                                    <div>
                                        <span>24h</span>
                                        <strong className={getTrendClass(asset.change24hPct)}>
                                            {formatPercent(asset.change24hPct)}
                                        </strong>
                                    </div>
                                    <div>
                                        <span>Volume 24h</span>
                                        <strong>{formatCompactCurrency(asset.quoteVolume24h, "USD")}</strong>
                                    </div>
                                </div>

                                <div className="ativos-wallet-market-row">
                                    <span className={asset.marketAvailable ? "ativos-market-badge available" : "ativos-market-badge"}>
                                        {asset.marketAvailable ? "por OmexLab" : "Mercado indisponível"}
                                    </span>
                                    <span className="ativos-wallet-updated">
                                        <FiClock />
                                        {formatDateTime(asset.marketUpdatedAt)}
                                    </span>
                                </div>
                                </button>
                            );
                        })}
                    </div>
                </section>

                {selectedAsset && (
                    <section className="ativos-intelligence-section">
                        <div className="cliente-page-section-header">
                            <div>
                                <p>Análise</p>
                                <h2>Leitura do ativo selecionado</h2>
                            </div>
                            <span>{detailLoading ? "Carregando detalhe..." : detailError ? "Detalhe parcial disponível" : `Foco atual: ${selectedAsset.symbol}`}</span>
                        </div>

                        <div className="ativos-intelligence-grid">
                            <article className="cliente-page-panel dark ativos-focus-panel">
                                <div className="ativos-focus-header">
                                    <AssetIcon
                                        symbol={selectedAsset.symbol}
                                        imageUrl={selectedAssetImageUrl}
                                        className="ativos-focus-token"
                                    />
                                    <div>
                                        <p>Ativo em foco</p>
                                        <h3>{selectedAsset.symbol}</h3>
                                        {selectedAssetName && <span>{selectedAssetName}</span>}
                                    </div>
                                </div>
                                <div className="ativos-focus-price">
                                    <strong>{formatCurrency(toNumber(selectedMarket?.price), "USD")}</strong>
                                    <span className={getTrendClass(toNumber(selectedMarket?.change_24h_pct ?? selectedAsset.change24hPct))}>
                                        {formatPercent(toNumber(selectedMarket?.change_24h_pct ?? selectedAsset.change24hPct))} 24h
                                    </span>
                                </div>
                                <div className="ativos-focus-stats">
                                    <div>
                                        <span>Volume 24h</span>
                                        <strong>{formatCompactCurrency(toNumber(selectedMarket?.quote_volume_24h ?? selectedAsset.quoteVolume24h), "USD")}</strong>
                                    </div>
                                    <div>
                                        <span>High / Low 24h</span>
                                        <strong>
                                            {formatCurrency(toNumber(selectedMarket?.high_24h ?? selectedAsset.high24h), "USD")} / {formatCurrency(toNumber(selectedMarket?.low_24h ?? selectedAsset.low24h), "USD")}
                                        </strong>
                                    </div>
                                    <div>
                                        <span>Atualizado</span>
                                        <strong>{formatDateTime(getAssetDetailUpdatedAt(selectedDetail) || selectedAsset.marketUpdatedAt)}</strong>
                                    </div>
                                </div>
                            </article>

                            <article className="cliente-page-panel ativos-read-panel">
                                <div className="cliente-page-section-header">
                                    <div>
                                        <p>Sinais</p>
                                        <h2>Mercado e derivativos</h2>
                                    </div>
                                    <FiTrendingUp />
                                </div>
                                <div className="ativos-signal-list">
                                    <div>
                                        <FiTrendingUp />
                                        <span>Momentum</span>
                                        <strong className={getTrendClass(selectedAsset.change24hPct)}>{formatPercent(selectedAsset.change24hPct)}</strong>
                                    </div>
                                    <div>
                                        <FiLayers />
                                        <span>Spread book</span>
                                        <strong>{formatPercent(toNumber(selectedOrderBook?.spread_pct))}</strong>
                                    </div>
                                    <div>
                                        <FiZap />
                                        <span>Funding</span>
                                        <strong className={getTrendClass(toNumber(selectedDerivatives?.futures_context?.last_funding_rate))}>
                                            {formatPercent(toNumber(selectedDerivatives?.futures_context?.last_funding_rate) !== undefined
                                                ? toNumber(selectedDerivatives?.futures_context?.last_funding_rate)! * 100
                                                : undefined)}
                                        </strong>
                                    </div>
                                    <div>
                                        <FiTarget />
                                        <span>Open interest</span>
                                        <strong>{formatCompactCurrency(toNumber(selectedDerivatives?.futures_context?.current_open_interest), "USD")}</strong>
                                    </div>
                                </div>
                                <p className="ativos-panel-note">
                                    {selectedAsset.marketAvailable
                                        ? "Dados de mercado por OmexLab. Derivativos e book aparecem quando estão disponíveis."
                                        : "Este ativo está na carteira, mas ainda não tem telemetria de mercado suficiente em OmexLab."}
                                </p>
                            </article>

                        </div>
                    </section>
                )}

                <section className="ativos-context-grid">
                    <article className="cliente-page-panel ativos-feed-panel">
                        <div className="cliente-page-section-header">
                            <div>
                                <p>Macro</p>
                                <h2>Feed de contexto</h2>
                            </div>
                            <span>{matchedMacroFeed.length} notas</span>
                        </div>
                        <div className="ativos-feed-list">
                            {matchedMacroFeed.slice(0, 5).map((item, index) => {
                                const isMacroNote = item.type === "macro_note";
                                const macroNote = isMacroNote ? getMacroNote(item) : "";
                                const macroAssets = isMacroNote ? getMacroAssets(item) : [];
                                const macroTags = isMacroNote ? getMacroTags(item) : [];
                                const macroImages = isMacroNote ? getMacroImages(item) : [];
                                const itemEtfAssets = item.type === "etf_inflow"
                                    ? extractEtfAssets(item.payload as OmexProEtfInflowReport)
                                    : [];
                                const itemDate = item.type === "etf_inflow"
                                    ? item.payload?.reportDate || item.publishedAt || item.displayDate || item.sortDate
                                    : item.publishedAt || item.displayDate || item.sortDate;

                                return (
                                    <article className="ativos-feed-item" key={`${item.id || item.sourceId || index}`}>
                                        <div className="ativos-feed-item-header">
                                            <span className="ativos-feed-type">{getFeedTypeLabel(item)}</span>
                                            <small>{formatOmexDate(itemDate)}</small>
                                        </div>
                                        <strong>
                                            {item.type === "etf_inflow"
                                                ? `Fluxos de ETF - ${formatOmexDate(itemDate)}`
                                                : item.title || "Nota de mercado"}
                                        </strong>

                                        {isMacroNote ? (
                                            <>
                                                {macroNote && <p className="ativos-feed-full-note">{macroNote}</p>}
                                                {(macroAssets.length > 0 || macroTags.length > 0) && (
                                                    <div className="ativos-feed-metadata">
                                                        {macroAssets.map((asset) => (
                                                            <span className="asset" key={`asset-${asset}`}>{asset}</span>
                                                        ))}
                                                        {macroTags.map((tag) => (
                                                            <span key={`tag-${tag}`}>{tag}</span>
                                                        ))}
                                                    </div>
                                                )}
                                                {macroImages.length > 0 && (
                                                    <div className="ativos-feed-images">
                                                        {macroImages.slice(0, 4).map((imageUrl, imageIndex) => (
                                                            <img
                                                                key={imageUrl}
                                                                src={imageUrl}
                                                                alt={`${item.title || "Nota macro"} - imagem ${imageIndex + 1}`}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        ) : itemEtfAssets.length ? (
                                            <div className="ativos-feed-etf-flows">
                                                {itemEtfAssets.map((asset, assetIndex) => {
                                                    const flow = getEtfAssetFlowValue(asset);

                                                    return (
                                                        <span className={getFlowClass(flow)} key={`${getEtfAssetLabel(asset, assetIndex)}-${assetIndex}`}>
                                                            <b>{getEtfAssetLabel(asset, assetIndex)}</b>
                                                            {formatEtfFlow(flow)}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p>{item.summary || "Sem dados disponíveis."}</p>
                                        )}
                                    </article>
                                );
                            })}
                            {!matchedMacroFeed.length && (
                                <div className="cliente-page-empty">
                                    <p>Sem notas macro disponíveis no momento.</p>
                                </div>
                            )}
                        </div>
                    </article>

                    <article className="cliente-page-panel ativos-etf-panel">
                        <div className="cliente-page-section-header">
                            <div>
                                <p>Fluxos</p>
                                <h2>Flows em ETF</h2>
                            </div>
                            <FiBriefcase />
                        </div>
                        {etfAssets.length > 0 && (
                            <div className="ativos-etf-summary">
                                <div>
                                    <span>Fluxo líquido</span>
                                    <strong className={getFlowClass(etfFlowTotal)}>{formatEtfFlow(etfFlowTotal)}</strong>
                                </div>
                                <div>
                                    <span>Com entrada</span>
                                    <strong>{etfPositiveAssets}</strong>
                                </div>
                                <div>
                                    <span>Com saída</span>
                                    <strong>{etfNegativeAssets}</strong>
                                </div>
                            </div>
                        )}
                        <div className="ativos-etf-list">
                            {etfAssets.slice(0, 5).map((asset, index) => {
                                const label = getEtfAssetLabel(asset, index);
                                const flow = getEtfAssetFlowValue(asset);
                                const fundFlows = getEtfFundFlows(asset);
                                const barWidth = Math.max(3, (Math.abs(flow || 0) / etfMaxFlow) * 100);

                                return (
                                    <article className="ativos-etf-item" key={`${label}-${index}`}>
                                        <div className="ativos-etf-item-header">
                                            <span>{label}</span>
                                            <strong className={getFlowClass(flow)}>{formatEtfFlow(flow)}</strong>
                                        </div>
                                        <div className="ativos-etf-flow-track" aria-hidden="true">
                                            <span
                                                className={getFlowClass(flow)}
                                                style={{ width: `${barWidth}%` }}
                                            />
                                        </div>
                                        {fundFlows.length > 0 && (
                                            <div className="ativos-etf-funds">
                                                {fundFlows.slice(0, 4).map((fundFlow) => (
                                                    <span className={getFlowClass(fundFlow.value)} key={fundFlow.fund}>
                                                        <b>{fundFlow.fund}</b>
                                                        {formatEtfFlow(fundFlow.value)}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </article>
                                );
                            })}
                            {!etfAssets.length && (
                                <div className="cliente-page-empty">
                                    <p>Sem relatório de ETF disponível.</p>
                                </div>
                            )}
                        </div>
                        <p className="ativos-panel-note">
                            {etfReport?.source || "Fonte não informada"} · Relatório de {formatOmexDate(etfReport?.reportDate || etfReport?.publishedAt || etfReport?.collectedAt)} · Valores em milhões de dólares
                        </p>
                    </article>

                </section>
            </ClientePageShell>
            {modalDesconectar && <ModalDesconectar modal={modalDesconectar} />}
        </>
    );
};
